import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiChainService } from '../../../services/api/api-chain.service';
import { ProgramType } from '../../../common/enums/care-monitor/program-type';
import {
  Program,
  ProgramList,
} from '../../../common/models/care-monitor/program';
import { ToastService } from '../../../services/toast.service';
import { ProgramsItemComponent } from '../programs-item/programs-item.component';
import { SkeletonDirective } from '../../../directives/skeleton.directive';
import { InfiniteScrollDirective } from '../../../directives/infinite-scroll.directive';
import { PaginationHelper } from '../../../common/helpers/pagination.helper';
import { TranslateModule } from '@ngx-translate/core';
import { ProgramGroup } from '../../../common/enums/care-monitor/program-group';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../../../services/authentication.service';

@Component({
  selector: 'mec-programs-list',
  imports: [
    CommonModule,
    ProgramsItemComponent,
    SkeletonDirective,
    InfiniteScrollDirective,
    TranslateModule,
  ],
  providers: [ApiChainService],
  templateUrl: './programs-list.component.html',
  styleUrls: ['./programs-list.component.scss'],
})
export class ProgramsListComponent implements OnChanges, OnDestroy {
  @Input() programType: ProgramType;
  @Input() tenantToken: string;
  @Input() isActive: boolean;
  @Output() activeProgramsCountChange = new EventEmitter<number>();

  public programList: ProgramList = new ProgramList();
  public isLoading: boolean = true;

  private _reloadTimer: ReturnType<typeof setTimeout>;
  private _reloadIndex: number = 1;
  private _autoReloadStarted: boolean = false;
  private _reloadTimerPolicy: ReturnType<typeof setTimeout>;
  private _reloadIndexPolicy: number = 1;

  public subscription: Subscription;

  constructor(
    private _apiChainService: ApiChainService,
    private _toastService: ToastService,
    private _authService: AuthenticationService
  ) {
    this.subscription = this._authService.onLoggedInOut.subscribe(() => {
      if (!this._authService.isLogged) {
        if (this._reloadTimer) {
          clearTimeout(this._reloadTimer);
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public OnDestroy(): void {
    if (this._reloadTimer) {
      clearTimeout(this._reloadTimer);
    }
    if (this._reloadTimerPolicy) {
      clearTimeout(this._reloadTimerPolicy);
    }
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['tenantToken'] || changes['isActive']) {
      this.getProgramsList();

      if (this.programType === ProgramType.ACTIVE) {
        if (this._autoReloadStarted) {
          return;
        }
        this._scheduleNextReload();
      }
    }
  }

  public async getProgramsList(reset?: boolean): Promise<void> {
    if (reset) {
      if (this.programType === ProgramType.ACTIVE) {
        if (this._reloadTimer) {
          clearTimeout(this._reloadTimer);
        }
        this._scheduleNextReload();
      }
    }

    switch (this.programType) {
      case ProgramType.POLICY:
        await this._getPolicies(reset);
        break;
      case ProgramType.FINISHED:
        await this._getFinishedPrograms(reset);
        break;
      default:
        await this._getActivePrograms(reset);
    }
  }

  private async _getActivePrograms(
    reset?: boolean,
    showLoading: boolean = true
  ): Promise<void> {
    if (showLoading) {
      this.isLoading = true;
    }

    if (reset) {
      this.programList = new ProgramList();
    }

    try {
      const newPrograms = await this._apiChainService.getExecutionsPending(
        this.tenantToken
      );
      let filteredPrograms = newPrograms.filter(
        (e) =>
          e.metadata?.group &&
          e.metadata?.group !== ProgramGroup.POLICY_ACCEPTED &&
          e.metadata?.group !== ProgramGroup.POLICY_NOT_ACCEPTED
      );

      // Order putting policy group at the start
      filteredPrograms = filteredPrograms.sort((a, b) => {
        const isAPolicyGroup =
          a.metadata?.group === ProgramGroup.POLICY ? 1 : 0;
        const isBPolicyGroup =
          b.metadata?.group === ProgramGroup.POLICY ? 1 : 0;
        return isBPolicyGroup - isAPolicyGroup;
      });

      const programsNotAnswered = this.programList.programs.filter(
        (e) =>
          (e.metadata?.group && e.metadata?.group === ProgramGroup.INIT) ||
          (e.metadata?.group && e.metadata?.group === ProgramGroup.POLICY)
      );

      this.activeProgramsCountChange.emit(programsNotAnswered.length);

      if (
        !this._areProgramsEqual(this.programList.programs, filteredPrograms)
      ) {
        this.programList.programs = filteredPrograms;
      }
    } catch (error) {
      this._toastService.showError(error);
    }

    this.isLoading = false;
  }

  private async _getPolicies(reset?: boolean): Promise<void> {
    if (reset) {
      this.programList = new ProgramList();
      if (this._reloadTimerPolicy) {
        clearTimeout(this._reloadTimerPolicy);
      }
    }

    try {
      this.programList.programs =
        await this._apiChainService.getExecutionsPending(this.tenantToken);

      this.programList.programs = this.programList.programs.filter(
        (e) =>
          (e.metadata?.group &&
            e.metadata?.group?.includes(ProgramGroup.POLICY_ACCEPTED)) ||
          e.metadata?.group?.includes(ProgramGroup.POLICY_NOT_ACCEPTED)
      );

      this._reloadTimerPolicy = setTimeout(() => {
        this._getPolicies(true);
        this._reloadIndexPolicy++;
      }, 4000 * this._reloadIndexPolicy);
    } catch (error) {
      this._toastService.showError(error);
    }

    this.isLoading = false;
  }

  private async _getFinishedPrograms(reset?: boolean): Promise<void> {
    if (!this.isActive) {
      return;
    }

    this.programList.pagination.isLoadingMore = true;

    if (reset) {
      this.programList = new ProgramList();
    }

    try {
      const itemsToAdd = await this._apiChainService.getExecutionsCompleted(
        this.tenantToken,
        this.programList.pagination
      );

      this.programList = PaginationHelper.newHandlePagination<Program>(
        this.programList,
        'programs',
        itemsToAdd
      );
    } catch (error) {
      this._toastService.showError(error);
    }
    this.isLoading = false;
    this.programList.pagination.isLoadingMore = false;
  }

  private _areProgramsEqual(prev: Program[], next: Program[]): boolean {
    if (!prev && !next) return true;
    if ((!prev && next) || (prev && !next)) return false;
    if (prev.length !== next.length) return false;

    const prevIds = new Set(prev.map((p) => p.token));
    return next.every((p) => prevIds.has(p.token));
  }

  private _scheduleNextReload(): void {
    this._autoReloadStarted = true;
    const delay = 2000 * this._reloadIndex;

    this._reloadTimer = setTimeout(() => {
      this._reloadIndex++;
      this._getActivePrograms(false, false);
      this._scheduleNextReload();
    }, delay);
  }
}
